import { Alert, Spin } from "antd";
import type { Data } from "plotly.js";
import Plot, { Figure } from "react-plotly.js";
import NoData from "../../NewSite/Components/OptimizationConfigurationForm/NoData";
import type { PricePoint } from "./useDayAheadPrices";
import { plotlyDateAxis } from "../../../util/formatDate";

const ACTUAL_COLOR = "#1677ff";
const FORECAST_COLOR = "#fa8c16";

type PriceChartProps = {
  actual: PricePoint[];
  forecast: PricePoint[];
  title: string;
  yAxisTitle: string;
  isLoading?: boolean;
  error?: Error | null;
};

// Prices are step values holding until the next point, so the last one needs a
// closing point or it renders as a bare dot.
const closeLastStep = (points: PricePoint[]): PricePoint[] => {
  if (points.length < 2) {
    return points;
  }
  const last = points[points.length - 1];
  const step =
    last.timestamp.getTime() - points[points.length - 2].timestamp.getTime();
  return [
    ...points,
    { timestamp: new Date(last.timestamp.getTime() + step), value: last.value },
  ];
};

// Repeat the boundary point so the dashed forecast starts where the solid
// actual line ends instead of floating away from it.
const bridgeFromActual = (
  forecast: PricePoint[],
  actual: PricePoint[],
): PricePoint[] => {
  const lastActual = actual[actual.length - 1];
  const firstForecast = forecast[0];
  if (!lastActual || !firstForecast) {
    return forecast;
  }
  return firstForecast.timestamp > lastActual.timestamp
    ? [lastActual, ...forecast]
    : forecast;
};

const buildTrace = (
  points: PricePoint[],
  name: string,
  color: string,
  dashed: boolean,
): Data => ({
  x: points.map((point) => point.timestamp.getTime()),
  y: points.map((point) => point.value),
  type: "scatter",
  mode: "lines",
  line: {
    shape: "hv",
    color,
    width: 2,
    dash: dashed ? "dash" : "solid",
  },
  name,
  hovertemplate: `%{y:.2f}<extra>${name}</extra>`,
});

export const PriceChart: React.FC<PriceChartProps> = ({
  actual,
  forecast,
  title,
  yAxisTitle,
  isLoading,
  error,
}) => {
  if (error) {
    return <Alert message="Error" description={error.message} type="error" />;
  }

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-96">
        <Spin />
      </div>
    );
  }

  if (actual.length === 0 && forecast.length === 0) {
    return (
      <NoData message="No prices available for this bidding zone and range." />
    );
  }

  const data: Data[] = [
    buildTrace(closeLastStep(actual), "Actual", ACTUAL_COLOR, false),
    buildTrace(
      closeLastStep(bridgeFromActual(forecast, actual)),
      "Forecast",
      FORECAST_COLOR,
      true,
    ),
  ];

  const layout: Figure["layout"] = {
    title,
    xaxis: {
      ...plotlyDateAxis,
      title: "Time",
    },
    yaxis: {
      title: yAxisTitle,
      rangemode: "tozero",
      zeroline: true,
      zerolinecolor: "#d9d9d9",
    },
    legend: {
      orientation: "h",
    },
    hovermode: "x unified",
    margin: { t: 60, r: 20, b: 50, l: 70 },
  };

  return (
    <Plot
      data={data}
      layout={layout}
      useResizeHandler
      style={{ width: "100%", height: 460 }}
      config={{ displaylogo: false, responsive: true }}
    />
  );
};
